import * as Sentry from '@sentry/angular';

/**
 * Opens the Sentry user-feedback dialog, styled to fit the app.
 *
 * Shared rather than duplicated (HEC-303): the burger menu's
 * "Provide Feedback" item and the Help page's identically
 * labelled button must open the same dialog. The widget renders
 * into a shadow root, so its layout can only be reached by
 * injecting a stylesheet into that root once the form exists -
 * without it the dialog header sits under the notch on a device
 * with a safe-area inset and the message box keeps Sentry's
 * default height.
 *
 * `getFeedback` is a defaulted parameter purely as a test seam:
 * the Sentry module namespace is frozen, so a spec cannot spy on
 * it. Callers pass nothing.
 */
export async function openSentryFeedback(
  getFeedback: typeof Sentry.getFeedback = Sentry.getFeedback
): Promise<void> {
  const feedback = getFeedback();
  const form = await feedback?.createForm();
  if (!form) {
    return;
  }
  form.appendToDom();
  form.open();
  applyFeedbackFormStyles();
}

function applyFeedbackFormStyles(): void {
  const host = document.getElementById('sentry-feedback');
  const shadowRoot = host?.shadowRoot;
  if (!shadowRoot) return;

  const style = document.createElement('style');
  style.textContent = `
      .dialog__position {
        padding-top: env(safe-area-inset-top, 0px);
      }
      .dialog__content {
        flex: 1;
      }
      .form__right {
        flex: 1;
      }
      .form__top {
        flex: 1;
      }
      .form__top .form__label:last-of-type {
        flex: 1;
        display: flex;
        flex-direction: column;
      }
      .form__input--textarea {
        flex: 1;
        min-height: 150px;
      }
    `;
  shadowRoot.appendChild(style);
}
